import { useState } from 'react'

import CloseOutlinedIcon from '@ant-design/icons/CloseOutlined'
import LinkOutlinedIcon from '@ant-design/icons/LinkOutlined'
import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'

import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import Link from '@components/_mui/Link'
import Typography from '@components/_mui/Typography'

import { useMe } from '@shared/providers/src/MeProvider'
import { getTaskKindLabel, isMATask, MedicalRecordStatus, PossibleRedirects, TaskKind, TaskStatus } from '@shared/utils'

import { CreateRoiMedicalRecord, FaxRoiMedicalRecord } from '@pages/RoiMedicalRecords'
import { useROIMedicalRecord } from '@pages/RoiMedicalRecords/RoiMedicalRecords.hooks'
import CopyToClipboard from '@components/CopyToClipboard'

import StatusButton from '../../components/StatusButton'

export default function Header({ task, onClose, scrolled }) {
  const locked = [TaskStatus.Completed, TaskStatus.Expired].includes(task.status)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const title = task.escalationReason || getTaskKindLabel(task)

  if (isMobile) {
    return (
      <Stack spacing={1} sx={[styles.root, { alignItems: 'stretch' }, scrolled && styles.scrolled]}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Meta task={task} />
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <More task={task} />
            <Close onClick={onClose} />
          </Stack>
        </Stack>
        <Stack direction="row" sx={styles.titleRow}>
          <Typography variant="h4" sx={[styles.title, { flex: '1 1 auto' }]}>
            {title}
          </Typography>
          <StatusButton task={task} disabled={locked} />
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack direction="row" spacing={2} sx={[styles.root, scrolled && styles.scrolled]}>
      <Stack sx={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
        <Meta task={task} />
        <Typography variant="h3" sx={styles.title}>
          {title}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
        <StatusButton task={task} disabled={locked} />
        <More task={task} />
        <Box sx={{ height: 24, width: '1px', bgcolor: 'divider', mx: 0.5 }} />
        <Close onClick={onClose} />
      </Stack>
    </Stack>
  )
}

function Meta({ task }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
      <Identifier task={task} />
      {task.escalationReason && (
        <>
          <Box sx={{ height: 14, width: '1px', bgcolor: 'divider', flexShrink: 0 }} />
          <Typography noWrap sx={{ color: 'text.secondary' }}>
            {getTaskKindLabel(task)}
          </Typography>
        </>
      )}
    </Stack>
  )
}

function Identifier({ task }) {
  const theme = useTheme()
  return (
    <CopyToClipboard>
      {({ copy }) => (
        <Link
          onClick={() =>
            copy(
              `${import.meta.env.VITE_URL}/redirect?to=${import.meta.env.VITE_URL}/app/redirects/${PossibleRedirects.Task}?task=${task.identifier}`
            )
          }
          sx={styles.id}
        >
          {task.identifier}
          <LinkOutlinedIcon style={{ color: theme.palette.text.secondary, marginLeft: 4, fontSize: 14 }} />
        </Link>
      )}
    </CopyToClipboard>
  )
}

function Close({ onClick }) {
  return (
    <IconButton onClick={onClick} color="inherit">
      <CloseOutlinedIcon style={{ fontSize: 22 }} />
    </IconButton>
  )
}

function More({ task }) {
  const me = useMe()
  const [anchor, setAnchor] = useState(null)
  const [open, setOpen] = useState(false)

  const isROIMedicalRecord = isMATask(task) && task?.kind === TaskKind.RoiPDF && task.assigneeId === me.id
  const isDone = [TaskStatus.Completed, TaskStatus.Expired].includes(task.status)

  if (isDone || !isROIMedicalRecord) return null

  return (
    <>
      <IconButton ref={setAnchor} size="small" color="inherit" onClick={() => setOpen((o) => !o)}>
        <MoreOutlinedIcon style={{ fontSize: 22 }} />
      </IconButton>
      <Menu
        keepMounted
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ROIMedicalRecordMenuItem task={task} onClose={() => setOpen(false)} />
      </Menu>
    </>
  )
}

function ROIMedicalRecordMenuItem({ task, onClose }) {
  const [createRoiOpen, setCreateRoiOpen] = useState(false)
  const [resendRoiOpen, setResendRoiOpen] = useState(false)

  const hasMedicalRecord = Boolean(task?.relatedModelId)

  const { data, isPending } = useROIMedicalRecord(task.patientId, task.relatedModelId, {
    enabled: hasMedicalRecord,
  })

  if (hasMedicalRecord) {
    const isFailed = data?.status === MedicalRecordStatus.FaxFailed
    return (
      <Tooltip title={isFailed ? '' : 'Resend is only available when fax failed'}>
        <div>
          <MenuItem
            disabled={isPending || !isFailed}
            onClick={() => {
              onClose()
              setResendRoiOpen(true)
            }}
          >
            Resend ROI Medical Record
          </MenuItem>
          <FaxRoiMedicalRecord patientId={task.patientId} data={data} open={resendRoiOpen} onClose={() => setResendRoiOpen(false)} />
        </div>
      </Tooltip>
    )
  }

  return (
    <>
      <MenuItem
        onClick={() => {
          onClose()
          setCreateRoiOpen(true)
        }}
      >
        Create ROI Medical Record
      </MenuItem>
      <CreateRoiMedicalRecord patientId={task.patientId} task={task} open={createRoiOpen} onClose={() => setCreateRoiOpen(false)} />
    </>
  )
}

Header.Loading = function HeaderLoading({ onClose }) {
  return (
    <Stack direction="row" spacing={2} sx={styles.root}>
      <Stack sx={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
        <Link sx={styles.id}>
          <Skeleton width={100} />
        </Link>
        <Typography variant="h3" sx={styles.title}>
          <Skeleton width={300} />
        </Typography>
      </Stack>
      <Close onClick={onClose} />
    </Stack>
  )
}

const styles = {
  root: {
    px: 3,
    py: 2,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid',
    borderColor: 'transparent',
    transition: 'border-color 150ms ease-in-out, background-color 150ms ease-in-out',
  },
  titleRow: {
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1,
  },
  scrolled: {
    borderColor: 'divider',
    backgroundColor: 'background.paper',
  },
  id: {
    color: 'text.secondary',
    cursor: 'pointer',
    fontSize: 14,
    fontFamily: 'monospace',
    width: 'fit-content',
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.2,
  },
}
